/**
 * The first node selected (Feature 80 D1, D19): how the template starts and
 * the capability it starts from; on a schedule, when it runs, said with the
 * schedule builder (Feature 81), and the fixed input its first task runs
 * with at each fire, one row per input field of the capability, a required
 * one marked (D5). How it starts is chosen while the template is new and
 * read-only once saved; the capability, the schedule, and the input stay
 * editable. While the template is unsaved, Remove first node
 * empties the canvas of it (D3).
 */
import type { LinkageStart } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Field } from '../../ui/Field.js';
import { Stack, Typography } from '../../ui/finixt.js';
import { Select, type SelectOption } from '../../ui/Select.js';
import type { Draft } from './draft.js';
import { removeFirst, setStartInput } from './first.js';
import { InspectorPanel } from './InspectorPanel.js';
import { mappableFields, type Schemas } from './references.js';
import { ScheduleBuilder } from './ScheduleBuilder.js';
import type { Selection } from './selection.js';
import { CALL, COMPLETION, isSchedule } from './start.js';
import { ValueInput } from './ValueInput.js';

/** The three starts, each saying what it does. */
const STARTS = [
  {
    value: 'completion',
    label: 'When it completes',
    description: 'after its answer; steps run as the caller',
  },
  {
    value: 'call',
    label: 'When it is called',
    description: 'at the request, beside it; steps independent',
  },
  {
    value: 'schedule',
    label: 'On a schedule',
    description: 'a cron runs it; steps run as the automation user',
  },
] as const satisfies readonly SelectOption[];

/** A line of help under each start (the wireframe's choice). */
const HELP: Record<LinkageStart['kind'], string> = {
  completion:
    'A run starts each time a client’s task of this capability completes. Its input reads as input.…, its result as steps.first.result.….',
  call: 'A run starts as a client calls it. Every step after it is independent and reads the call’s input only.',
  schedule:
    'At each fire the platform runs this capability with the input below, as each organization’s automation identity; the chain runs after it, as after a completion.',
};

/** The start a kind picked while new makes, keeping a schedule's cron and input. */
function startOf(kind: string, current: LinkageStart): LinkageStart {
  if (kind === 'schedule') {
    return { kind: 'schedule', cron: current.cron ?? null, input: current.input ?? {} };
  }
  return kind === 'call' ? CALL : COMPLETION;
}

/** A saved template's start: the field's place, read-only. */
function FixedStart({ label }: { label: string }) {
  return (
    <div
      aria-label="How it starts"
      style={{
        boxSizing: 'border-box',
        padding: 'var(--pad-input)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-input)',
        background: 'var(--surface-sunken)',
        fontSize: 'var(--text-body-md)',
        color: 'var(--text-muted)',
      }}
    >
      {label}
    </div>
  );
}

export function FirstInspector({
  draft,
  isNew,
  capabilities,
  schemas,
  onChange,
  onSelect,
}: {
  draft: Draft;
  isNew: boolean;
  capabilities: readonly SelectOption[];
  schemas: Schemas;
  onChange: (draft: Draft) => void;
  onSelect: (selection: Selection) => void;
}) {
  const schedule = isSchedule(draft.start);
  const chosen = STARTS.find((s) => s.value === draft.start.kind) ?? STARTS[0];
  const cron = draft.start.cron ?? '';
  const schema = schemas.get(draft.firstCapabilityId);
  const fields = mappableFields(schema);
  const input = draft.start.input ?? {};
  return (
    <InspectorPanel>
      <Typography variant="eyebrow">First node</Typography>
      {isNew ? (
        <Field id="first-start" label="How it starts">
          <Select
            id="first-start"
            value={draft.start.kind}
            options={STARTS}
            onChange={(kind) => onChange({ ...draft, start: startOf(kind, draft.start) })}
          />
        </Field>
      ) : (
        <Field label="How it starts" hint="fixed once saved">
          <FixedStart label={chosen.label} />
        </Field>
      )}
      <Field id="first-capability" label="Capability" required>
        <Select
          searchable
          id="first-capability"
          value={draft.firstCapabilityId}
          onChange={(value) => onChange({ ...draft, firstCapabilityId: value })}
          options={capabilities}
        />
      </Field>
      <Typography variant="meta">{HELP[draft.start.kind]}</Typography>
      {schedule && (
        <Field
          id="first-when"
          label="When it runs"
          required
          hint="An organization may set its own when it switches the template on."
        >
          <ScheduleBuilder
            id="first-when"
            cron={cron}
            onChange={(next) => onChange({ ...draft, start: { ...draft.start, cron: next } })}
          />
        </Field>
      )}
      {schedule && (
        <Stack gap={7}>
          <Typography variant="body" style={{ fontWeight: 'var(--weight-semibold)' }}>
            Input at each fire
          </Typography>
          {!schema && (
            <Typography variant="meta">
              This capability’s input fields are not known here; the agent checks the input when the
              template is saved.
            </Typography>
          )}
          {fields.map((f) => (
            <Field
              key={f.name}
              id={`first-input-${f.name}`}
              label={f.name}
              required={f.required}
              hint={f.required && !(f.name in input) ? 'required: it needs a value' : undefined}
            >
              <ValueInput
                label={`Input ${f.name}`}
                value={input[f.name]}
                onChange={(value) => onChange(setStartInput(draft, f.name, value))}
              />
            </Field>
          ))}
        </Stack>
      )}
      {isNew && (
        <Button
          variant="danger"
          size="sm"
          onClick={() => {
            onChange(removeFirst(draft));
            onSelect(null);
          }}
        >
          Remove first node
        </Button>
      )}
    </InspectorPanel>
  );
}
